<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrBadge from './HrBadge.vue';

/**
 * A single tab within an HrTabs bar.
 *
 * Supports M3 notification badges via the `badge` prop:
 *   - true        → small dot (signals presence, no count)
 *   - 3           → large badge showing the count (capped at 999+ internally)
 * Badge floats top-trailing on the icon; renders inline after the label for icon-less tabs.
 *
 * @status recommended
 * @group navigation
 * @useWhen Defining a single tab within an HrTabs bar
 * @within hr-tabs
 * @related HrTabs
 * @see https://quasar.dev/vue-components/tabs Quasar q-tab API
 */
defineOptions({ name: 'HrTab' });

const props = defineProps({
  /**
   * Identifier that links this tab to its HrTabPanel.
   * Required when `to` is not set; omit freely when using route-based tabs.
   */
  name: {
    type: [String, Number],
    default: null,
  },
  /** Tab label text. */
  label: {
    type: String,
    default: null,
  },
  /** Material icon name to display above the label. */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Route destination. When provided, renders as a q-route-tab (router-link); otherwise q-tab.
   * Accepts the same value as Vue Router's `to` prop — string path or route object.
   */
  to: {
    type: [String, Object],
    default: null,
  },
  /**
   * Use exact route matching (only meaningful when `to` is set).
   */
  exact: {
    type: Boolean,
    default: false,
  },
  /** Disables the tab. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Notification badge. false = none, true = small dot, number = count (capped at 999+ internally).
   */
  badge: {
    type: [Boolean, Number],
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const hasBadge = computed(() => props.badge !== false);
const badgeCount = computed(() => (typeof props.badge === 'number' ? props.badge : null));

const rootComponent = computed(() => (props.to !== null ? 'q-route-tab' : 'q-tab'));

const effectiveProps = computed(() => {
  // When a badge is active we take over icon/label rendering in the slot so we can
  // position the badge relative to the icon — suppress them from q-tab's own props
  // to avoid double-rendering.
  const base = {
    ...(props.name !== null && { name: props.name }),
    disable: props.disable,
    ...(props.to !== null && { to: props.to, exact: props.exact }),
    ...(!hasBadge.value && props.icon !== null && { icon: props.icon }),
    ...(!hasBadge.value && props.label !== null && { label: props.label }),
  };
  if (hasTheme.value) return { ...base, ...attrs };
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <component
    :is="rootComponent"
    v-bind="effectiveProps"
  >
    <template v-if="hasBadge">
      <!-- Icon tab: badge floats top-trailing on the icon -->
      <template
        v-if="props.icon !== null"
      >
        <div
          class="hr-tab__icon-wrap"
        >
          <q-icon
            :name="props.icon"
            class="q-tab__icon"
          />
        </div>
        <hr-badge :count="badgeCount" />
      </template>
      <!-- Label-only tab: badge renders inline after the label text -->
      <div
        v-else-if="props.label !== null"
        class="hr-tab__label-badge"
      >
        <span class="q-tab__label">{{ props.label }}</span>
        <hr-badge
          :count="badgeCount"
          inline
        />
      </div>
      <!-- Label below icon when both are present -->
      <span
        v-if="props.icon !== null && props.label !== null"
        class="q-tab__label"
      >{{ props.label }}</span>
    </template>
    <!-- @slot default Custom tab content when `icon` and `label` props are insufficient. -->
    <slot />
  </component>
</template>

<style lang="scss" scoped>
.hr-tab__icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hr-tab__label-badge {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
